import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { aiAPI, casesAPI, documentsAPI } from '../lib/api';
import { 
  Sparkles, 
  Send,
  FileText,
  MessageSquare,
  Wand2,
  Loader2,
  Upload,
  FolderOpen,
  Search,
  Filter,
  X,
  Briefcase,
  ChevronDown,
  Eye,
  CheckCircle2,
  FileIcon,
  FileImage,
  FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { FeatureGate } from '../components/common/FeatureGate';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const getFileIcon = (mimeType) => {
  if (mimeType?.includes('image')) return FileImage;
  if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return FileSpreadsheet;
  return FileIcon;
};

export const AIAssistant = () => {
  const { hasFeature } = useAuth();
  const [activeTab, setActiveTab] = useState('chat');
  const fileInputRef = useRef(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Document analysis state
  const [docText, setDocText] = useState('');
  const [analysisType, setAnalysisType] = useState('summary');
  const [analysisResult, setAnalysisResult] = useState('');
  const [analysisLoading, setAnalysisLoading] = useState(false);
  
  // Upload and case document states
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showDocumentBrowser, setShowDocumentBrowser] = useState(false);
  
  // Case filtering states
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [allDocuments, setAllDocuments] = useState([]);

  // Draft assist state
  const [draftType, setDraftType] = useState('');
  const [draftDetails, setDraftDetails] = useState('');
  const [draftResult, setDraftResult] = useState('');
  const [draftLoading, setDraftLoading] = useState(false);

  // Load cases on mount
  useEffect(() => {
    loadCases();
  }, []);

  const loadCases = async () => {
    try {
      const response = await casesAPI.getAll();
      setCases(response.data || []);
    } catch (error) {
      console.error('Error loading cases:', error);
    }
  };

  // Load documents when case changes or show all
  useEffect(() => {
    if (showDocumentBrowser) {
      loadDocuments();
    }
  }, [selectedCase, showDocumentBrowser]);

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try {
      if (selectedCase) {
        // Get documents for specific case
        const response = await documentsAPI.getAll({ case_id: selectedCase });
        setCaseDocuments(response.data || []);
      } else {
        // Get all documents
        const response = await documentsAPI.getAll();
        setAllDocuments(response.data || []);
        setCaseDocuments(response.data || []);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleChat = async () => {
    if (!chatInput.trim()) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await aiAPI.chat(chatInput);
      const assistantMessage = { role: 'assistant', content: response.data.response };
      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadLoading(true);
    setUploadedFile({ name: file.name, type: file.type, size: file.size });

    try {
      // Read file content
      const reader = new FileReader();
      reader.onload = async (event) => {
        const content = event.target?.result;
        
        if (file.type === 'application/pdf') {
          // For PDFs, we'll send to backend for extraction
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const response = await aiAPI.extractDocument(formData);
            setDocText(response.data.text || 'Unable to extract text from PDF');
            toast.success('PDF text extracted successfully');
          } catch (err) {
            console.error('PDF extraction error:', err);
            setDocText('[PDF content - text extraction pending]');
            toast.info('PDF uploaded. Click Analyze to process.');
          }
        } else if (file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
          // Plain text files
          setDocText(content);
          toast.success('File loaded successfully');
        } else {
          // For other files, show placeholder
          setDocText(`[File: ${file.name}]\nType: ${file.type}\nSize: ${(file.size / 1024).toFixed(2)} KB\n\n[Content will be analyzed]`);
          toast.info('File uploaded. Click Analyze to process.');
        }
        setUploadLoading(false);
      };
      
      if (file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
        reader.readAsText(file);
      } else {
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
      setUploadLoading(false);
    }
  };

  const handleSelectDocument = async (doc) => {
    setShowDocumentBrowser(false);
    setUploadedFile({ name: doc.name, type: doc.mime_type, size: 0, fromCase: true });
    
    // Try to load document content
    if (doc.file_url) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const contentType = response.headers.get('content-type');
          
          if (contentType?.includes('text') || doc.name.endsWith('.txt') || doc.name.endsWith('.md')) {
            const text = await response.text();
            setDocText(text);
            toast.success(`Document "${doc.name}" loaded`);
          } else {
            setDocText(`[Document: ${doc.name}]\nType: ${doc.mime_type || 'Unknown'}\n\nThis document is ready for AI analysis.`);
            toast.success(`Document "${doc.name}" selected for analysis`);
          }
        }
      } catch (error) {
        console.error('Error loading document:', error);
        setDocText(`[Document: ${doc.name}]\nSelected for analysis.`);
      }
    } else {
      setDocText(`[Document: ${doc.name}]\n${doc.description || 'No description available'}`);
    }
  };

  const handleAnalyze = async () => {
    if (!docText.trim()) return;

    setAnalysisLoading(true);
    try {
      const response = await aiAPI.analyzeDocument(docText, analysisType);
      setAnalysisResult(response.data.analysis);
    } catch (error) {
      console.error('Analysis error:', error);
      setAnalysisResult('Sorry, I encountered an error analyzing the document.');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleDraft = async () => {
    if (!draftType.trim() || !draftDetails.trim()) return;

    setDraftLoading(true);
    try {
      const response = await aiAPI.draftAssist(draftType, draftDetails);
      setDraftResult(response.data.draft);
    } catch (error) {
      console.error('Draft error:', error);
      setDraftResult('Sorry, I encountered an error creating the draft.');
    } finally {
      setDraftLoading(false);
    }
  };

  const clearDocument = () => {
    setUploadedFile(null);
    setDocText('');
    setAnalysisResult('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Filter documents by search
  const filteredDocuments = caseDocuments.filter(doc => 
    doc.name?.toLowerCase().includes(documentSearch.toLowerCase()) ||
    doc.description?.toLowerCase().includes(documentSearch.toLowerCase())
  );

  if (!hasFeature('ai')) {
    return (
      <FeatureGate feature="ai">
        <div className="h-[600px] flex items-center justify-center">
          <p>AI Assistant content</p>
        </div>
      </FeatureGate>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ai-assistant-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
          <span className="gradient-text">AI Assistant</span>
          <Sparkles className="h-6 w-6 text-purple-500" />
        </h1>
        <p className="text-slate-500 mt-1">Your intelligent legal workflow assistant powered by GPT-5.2</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="chat" className="gap-2" data-testid="tab-chat">
            <MessageSquare className="h-4 w-4" />
            Chat
          </TabsTrigger>
          <TabsTrigger value="analyze" className="gap-2" data-testid="tab-analyze">
            <FileText className="h-4 w-4" />
            Analyze
          </TabsTrigger>
          <TabsTrigger value="draft" className="gap-2" data-testid="tab-draft">
            <Wand2 className="h-4 w-4" />
            Draft
          </TabsTrigger>
        </TabsList>

        {/* Chat Tab */}
        <TabsContent value="chat">
          <Card className="h-[600px] flex flex-col">
            <CardHeader className="border-b">
              <CardTitle className="text-lg">Legal Assistant Chat</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0">
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {chatMessages.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      <Sparkles className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <p>Start a conversation with your AI legal assistant</p>
                      <p className="text-sm mt-2">Ask about legal research, case summaries, or practice management</p>
                    </div>
                  )}
                  {chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`ai-message ${msg.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'}`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="ai-message ai-message-assistant">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  )}
                </div>
              </ScrollArea>
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Ask me anything about legal workflows..."
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleChat()}
                    disabled={chatLoading}
                    data-testid="chat-input"
                  />
                  <Button onClick={handleChat} disabled={chatLoading || !chatInput.trim()} data-testid="send-chat-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analyze Tab - Enhanced */}
        <TabsContent value="analyze">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Document Source</CardTitle>
                  {uploadedFile && (
                    <Button variant="ghost" size="sm" onClick={clearDocument}>
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Document Source Options */}
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".pdf,.txt,.doc,.docx,.md,.rtf"
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <Button 
                    variant="outline" 
                    className="h-20 flex-col gap-2"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadLoading}
                    data-testid="upload-doc-btn"
                  >
                    {uploadLoading ? (
                      <Loader2 className="h-6 w-6 animate-spin" />
                    ) : (
                      <Upload className="h-6 w-6 text-blue-600" />
                    )}
                    <span className="text-sm">Upload Document</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex-col gap-2"
                    onClick={() => setShowDocumentBrowser(true)}
                    data-testid="browse-docs-btn"
                  >
                    <FolderOpen className="h-6 w-6 text-green-600" />
                    <span className="text-sm">Browse Case Docs</span>
                  </Button>
                </div>

                {/* Selected File Indicator */}
                {uploadedFile && (
                  <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                    <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">{uploadedFile.name}</p>
                      <p className="text-xs text-slate-500">
                        {uploadedFile.fromCase ? 'From case documents' : uploadedFile.type}
                      </p>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  </div>
                )}

                {/* Document Text Area */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Document Content
                  </label>
                  <Textarea
                    value={docText}
                    onChange={(e) => setDocText(e.target.value)}
                    placeholder="Paste document text here, upload a file, or select from case documents..."
                    rows={10}
                    className="font-mono text-sm"
                    data-testid="doc-text-input"
                  />
                </div>

                {/* Analysis Controls */}
                <div className="flex gap-2">
                  <Select value={analysisType} onValueChange={setAnalysisType}>
                    <SelectTrigger className="flex-1" data-testid="analysis-type-select">
                      <SelectValue placeholder="Analysis Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="summary">Summary</SelectItem>
                      <SelectItem value="risks">Risk Analysis</SelectItem>
                      <SelectItem value="extraction">Data Extraction</SelectItem>
                      <SelectItem value="clauses">Clause Analysis</SelectItem>
                      <SelectItem value="compliance">Compliance Check</SelectItem>
                      <SelectItem value="comparison">Compare Documents</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={handleAnalyze} 
                    disabled={analysisLoading || !docText.trim()}
                    className="min-w-[100px]"
                    data-testid="analyze-btn"
                  >
                    {analysisLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Analyze
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Analysis Result */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Analysis Result</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[450px]">
                  {analysisResult ? (
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg font-sans">{analysisResult}</pre>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <p>Analysis results will appear here</p>
                      <p className="text-sm mt-2">Upload a document or select one from your cases</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Draft Tab */}
        <TabsContent value="draft">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Draft Request</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="form-label">Document Type</label>
                  <Input
                    value={draftType}
                    onChange={(e) => setDraftType(e.target.value)}
                    placeholder="e.g., Non-Disclosure Agreement, Demand Letter, Contract..."
                    data-testid="draft-type-input"
                  />
                </div>
                <div>
                  <label className="form-label">Details & Requirements</label>
                  <Textarea
                    value={draftDetails}
                    onChange={(e) => setDraftDetails(e.target.value)}
                    placeholder="Describe the parties involved, key terms, specific clauses needed..."
                    rows={8}
                    data-testid="draft-details-input"
                  />
                </div>
                <Button 
                  onClick={handleDraft} 
                  disabled={draftLoading || !draftType.trim() || !draftDetails.trim()}
                  className="w-full"
                  data-testid="generate-draft-btn"
                >
                  {draftLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
                  Generate Draft
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Generated Draft</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {draftResult ? (
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg">{draftResult}</pre>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Wand2 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                      <p>Your draft will appear here</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Document Browser Dialog */}
      <Dialog open={showDocumentBrowser} onOpenChange={setShowDocumentBrowser}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Browse Case Documents
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-3">
              <div className="flex-1">
                <Select value={selectedCase || 'all'} onValueChange={(v) => setSelectedCase(v === 'all' ? null : v)}>
                  <SelectTrigger data-testid="case-filter-select">
                    <Briefcase className="h-4 w-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Filter by case" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Documents</SelectItem>
                    {cases.map(c => (
                      <SelectItem key={c.case_id} value={c.case_id}>
                        {c.title || c.case_number || c.case_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search documents..."
                  value={documentSearch}
                  onChange={(e) => setDocumentSearch(e.target.value)}
                  className="pl-9"
                  data-testid="doc-search-input"
                />
              </div>
            </div>

            {/* Document List */}
            <ScrollArea className="h-[400px] border rounded-lg">
              {documentsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p>No documents found</p>
                  {selectedCase && (
                    <p className="text-sm mt-1">Try selecting a different case or "All Documents"</p>
                  )}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredDocuments.map((doc) => {
                    const FileTypeIcon = getFileIcon(doc.mime_type);
                    const caseInfo = cases.find(c => c.case_id === doc.case_id);
                    
                    return (
                      <div
                        key={doc.document_id}
                        className="p-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3 transition-colors"
                        onClick={() => handleSelectDocument(doc)}
                        data-testid={`doc-item-${doc.document_id}`}
                      >
                        <div className="h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileTypeIcon className="h-5 w-5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{doc.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {caseInfo && (
                              <Badge variant="outline" className="text-xs">
                                <Briefcase className="h-3 w-3 mr-1" />
                                {caseInfo.title || caseInfo.case_number}
                              </Badge>
                            )}
                            <span className="text-xs text-slate-500">{doc.type || 'Document'}</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIAssistant;
